export const formatItineraryForAnalytics = (itinerary, paymentSource) => {
  if (!itinerary) {
    return {}
  }

  const items = []

  if (itinerary?.ferries?.length) {
    itinerary.ferries.forEach(ferry => {
      if (ferry?.crossings?.length) {
        ferry?.crossings?.forEach(crossing => {
          items.push({
            item_name: `ferry crossing ${crossing?.routeCode || ''}`,
            item_id: ferry.rph,
            price: crossing?.priceData?.total,
            item_brand: 'Red Funnel',
            item_category: 'Ferry',
            quantity: 1,
          })
        })
      }
    })
  }
  if (itinerary?.hotels?.length) {
    itinerary.hotels.forEach(hotel => {
      items.push({
        item_name: hotel.hotelName || hotel.hotelCode || '',
        item_id: hotel.bookingReference || '',
        price: hotel.price || '',
        item_brand: hotel.hotelCode || 'Red Funnel',
        item_category: 'Accommodations',
        quantity: 1,
      })
    })
  }

  if (itinerary?.attractions?.length) {
    itinerary.attractions.forEach(attraction => {
      items.push({
        item_name: attraction.description || '',
        item_id: attraction.resourceCode,
        price: attraction.price || '',
        item_brand: attraction.supplierCode || '',
        item_category: 'Attractions',
        quantity: attraction.amount || 1,
      })
    })
  }

  return {
    payment_type: paymentSource,
    ecommerce: {
      currency: 'GBP',
      value: itinerary?.grandTotal ? +itinerary.grandTotal : '',
      affiliation: 'Red Funnel',
      transaction_id: itinerary?.bookingNumber,
      items,
    },
  }
}

const hashEmail = async email => {
  const encoder = new TextEncoder()
  const data = encoder.encode(email.trim().toLowerCase())
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray
    .map(val => val.toString(16).padStart(2, '0'))
    .join('')
  return hashHex
}

export const formatEpsilonConversionForAnalytics = async (itinerary, user) => {
  if (!itinerary) {
    return {}
  }

  const items = []

  if (itinerary?.ferries?.length) {
    itinerary.ferries.forEach(ferry => {
      if (ferry?.crossings?.length) {
        ferry?.crossings?.forEach(crossing => {
          items.push({
            product_id: crossing?.routeCode,
            item_amount: crossing?.priceData?.total,
            item_quantity: '1',
            item_discount: crossing?.priceData?.discount,
          })
        })
      }
      if (
        ferry?.cancellationProtection &&
        ferry?.cancellationProtection?.included
      ) {
        items.push({
          product_id:
            ferry?.cancellationProtection?.resourceCode ||
            ferry?.cancellationProtection?.supplierCode ||
            '',
          item_amount: `${ferry?.cancellationProtection?.price  }`,
          item_quantity: '1',
          item_discount: '0.00',
        })
      }
    })
  }
  if (itinerary?.donations?.length) {
    itinerary.donations.forEach(donation => {
      items.push({
        product_id: donation?.resourceCode || donation?.supplierCode || '',
        item_amount: `${donation.price}` || '',
        item_quantity: '1',
        item_discount: '0.00',
      })
    })
  }
  if (itinerary?.hotels?.length) {
    itinerary.hotels.forEach(hotel => {
      items.push({
        product_id: hotel.hotelCode || hotel.resourceCode || '',
        item_amount: `${hotel.price}` || '',
        item_quantity: '1',
        item_discount: hotel.discount || '',
      })
    })
  }

  if (itinerary?.attractions?.length) {
    itinerary.attractions.forEach(attraction => {
      items.push({
        product_id: attraction.resourceCode || '',
        item_amount: `${attraction.price}` || '',
        item_quantity: `${attraction.amount}` || '1',
        item_discount: attraction.discount || '',
      })
    })
  }

  const base = {
    dtmc_transaction_id: itinerary.bookingNumber,
    dtm_conv_val: itinerary.totalCost,
    dtm_conv_curr: 'GBP',
    dtmc_site_language: 'EN',
    dtm_items: items,
  }

  if (user) {
    const hashedEmail = await hashEmail(user.email)
    return {
      ...base,
      dtm_user_id: user.customerNumber,
      dtm_email_hash: hashedEmail,
      dtmc_client_defined_country: user.countryName ?? '',
    }
  }

  return base
}

export const formatEpsilonActionConversionForAnalytics = async user => {
  if (!user) {
    return {}
  }
  const hashedEmail = await hashEmail(user.email)
  return {
    dtm_user_id: user.customerNumber,
    dtm_email_hash: hashedEmail,
    dtmc_client_defined_country: user.countryName ?? '',
    dtmc_site_language: 'EN',
  }
}

const getPromoId = (pathname, length) => {
  switch (true) {
    case pathname.includes('/my-account'):
      return '942'
    case pathname.includes('/payment'):
      return '8'
    case pathname.includes('/review'):
      return '6'
    case pathname.includes('/customer-details'):
      return '6'
    case pathname.includes('/summary'):
      return '6'
    case length === 1:
      return '2'
    case length === 2:
      return '3'
    case length >= 3:
      return '5'
    default:
      return '1'
  }
}

export const formatEpsilonPageVisitForAnalytics = async (user, pathname) => {
  if (
    pathname.includes('agent-portal') ||
    pathname.includes('contact-centre') ||
    pathname.includes('book/summary')
  )
    return {}

  const segments = pathname.split('/').filter(Boolean)
  const [dept = 'home', category = '', ...rest] = segments
  const product = rest.join(', ')
  const promoId = getPromoId(pathname, segments?.length)

  const base = {
    dtmc_department: dept,
    dtmc_category: category,
    dtmc_product_id: product,
    promo_id: promoId,
    dtmc_site_language: 'EN',
  }

  if (user) {
    const hashedEmail = await hashEmail(user.email)
    return {
      ...base,
      dtm_user_id: user.customerNumber,
      dtm_email_hash: hashedEmail,
      dtmc_client_defined_country: user.countryName ?? '',
    }
  }

  return base
}
